// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Checkbox, Group, Modal, NumberInput, Select, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  RPM_ASYNC_ACTIVITY_TYPES,
  RPM_OBSERVATION_CODES,
  RPM_SYNC_CONTACT_METHODS,
  type RpmAsyncActivityType,
  type RpmSyncContactMethod,
} from '@polarissleep/shared';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { RemoteMonitoringEntry } from '../../hooks/useRemoteMonitoringQueue';
import { RPM_NOTE_DETAIL_SYSTEM, upsertRpmObservation } from '../../utils/rpmFhir';
import { buildRpmNoteCommunication } from '../../utils/rpmNotes';
import { withTenantScope } from '../../utils/tenant';

interface RpmAddNoteModalProps {
  entry: RemoteMonitoringEntry | undefined;
  month: string;
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function toDatetimeLocalValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toIsoDateTime(value: string): string {
  const date = value ? new Date(value) : new Date();
  return Number.isFinite(date.getTime()) ? date.toISOString() : new Date().toISOString();
}

function buildChartNote(input: {
  asyncReviewed: boolean;
  daysReviewed: number | undefined;
  asyncMinutes: number | undefined;
  asyncActivityType: RpmAsyncActivityType;
  syncContact: boolean;
  syncMethod: RpmSyncContactMethod;
  syncMinutes: number | undefined;
  contactIso: string;
  note: string;
}): string {
  const lines = [`RPM note recorded ${new Date(input.contactIso).toLocaleString()}.`];
  if (input.asyncReviewed) {
    lines.push(
      `Async review: ${input.asyncMinutes ?? 0} minutes, ${input.daysReviewed ?? 0} days reviewed, ${input.asyncActivityType}.`
    );
  }
  if (input.syncContact) {
    lines.push(`Sync contact: ${input.syncMinutes ?? 0} minutes by ${input.syncMethod}.`);
  }
  if (input.note.trim()) {
    lines.push(`Note: ${input.note.trim()}`);
  }
  return lines.join('\n');
}

export function RpmAddNoteModal({ entry, month, opened, onClose, onSaved }: RpmAddNoteModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [asyncReviewed, setAsyncReviewed] = useState(false);
  const [daysReviewed, setDaysReviewed] = useState<number | ''>('');
  const [asyncMinutes, setAsyncMinutes] = useState<number | ''>('');
  const [asyncActivityType, setAsyncActivityType] = useState<RpmAsyncActivityType>('data-review');
  const [syncContact, setSyncContact] = useState(false);
  const [syncMethod, setSyncMethod] = useState<RpmSyncContactMethod>('phone');
  const [syncMinutes, setSyncMinutes] = useState<number | ''>('');
  const [contactDateTime, setContactDateTime] = useState(toDatetimeLocalValue(new Date()));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [summary, setSummary] = useState<string[]>([]);

  useEffect(() => {
    if (opened) {
      setAsyncReviewed(false);
      setDaysReviewed('');
      setAsyncMinutes('');
      setAsyncActivityType('data-review');
      setSyncContact(false);
      setSyncMethod('phone');
      setSyncMinutes('');
      setContactDateTime(toDatetimeLocalValue(new Date()));
      setNote('');
      setSummary([]);
      setError(undefined);
    }
  }, [opened]);

  const asyncActivityData = useMemo(() => [...RPM_ASYNC_ACTIVITY_TYPES], []);
  const syncMethodData = useMemo(() => [...RPM_SYNC_CONTACT_METHODS], []);

  const handleSave = async (): Promise<void> => {
    if (!entry?.patientId) {
      return;
    }
    if (!asyncReviewed && !syncContact && !note.trim()) {
      setError('Select async review, sync contact, or enter a note.');
      return;
    }
    if (asyncReviewed && (daysReviewed === '' || asyncMinutes === '')) {
      setError('Days reviewed and async minutes are required for async review.');
      return;
    }
    if (syncContact && syncMinutes === '') {
      setError('Sync minutes are required for sync contact.');
      return;
    }

    setSaving(true);
    setError(undefined);
    try {
      const contactIso = toIsoDateTime(contactDateTime);
      const chartNote = buildChartNote({
        asyncReviewed,
        daysReviewed: daysReviewed === '' ? undefined : daysReviewed,
        asyncMinutes: asyncMinutes === '' ? undefined : asyncMinutes,
        asyncActivityType,
        syncContact,
        syncMethod,
        syncMinutes: syncMinutes === '' ? undefined : syncMinutes,
        contactIso,
        note,
      });
      const savedSummary = ['Chart note Observation saved'];

      if (asyncReviewed && asyncMinutes !== '' && daysReviewed !== '') {
        await upsertRpmObservation(medplum, {
          patientId: entry.patientId,
          month,
          code: RPM_OBSERVATION_CODES.asyncMonitoringMinutes,
          accumulateInteger: true,
          existing: entry.rpmObservations[RPM_OBSERVATION_CODES.asyncMonitoringMinutes],
          effectiveDateTime: contactIso,
          value: { integer: asyncMinutes },
          components: [
            {
              code: { coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: 'days-reviewed' }] },
              valueInteger: daysReviewed,
            },
            {
              code: { coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: 'activity-type' }] },
              valueCodeableConcept: {
                coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: asyncActivityType }],
              },
            },
          ],
        });
        savedSummary.push('Async requirement updated');
      }

      if (syncContact && syncMinutes !== '') {
        await upsertRpmObservation(medplum, {
          patientId: entry.patientId,
          month,
          code: RPM_OBSERVATION_CODES.syncContactMinutes,
          accumulateInteger: true,
          existing: entry.rpmObservations[RPM_OBSERVATION_CODES.syncContactMinutes],
          effectiveDateTime: contactIso,
          value: { integer: syncMinutes },
          components: [
            {
              code: { coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: 'contact-method' }] },
              valueCodeableConcept: {
                coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: syncMethod }],
              },
            },
          ],
        });
        await upsertRpmObservation(medplum, {
          patientId: entry.patientId,
          month,
          code: RPM_OBSERVATION_CODES.lastContactDate,
          existing: entry.rpmObservations[RPM_OBSERVATION_CODES.lastContactDate],
          effectiveDateTime: contactIso,
          value: { dateTime: contactIso },
        });
        savedSummary.push('Last contact updated');
        savedSummary.push('Sync requirement updated');
      }

      const communication: Communication = buildRpmNoteCommunication({
        patientId: entry.patientId,
        patientName: entry.patientName,
        month,
        sent: contactIso,
        sender: profile ? createReference(profile) : undefined,
        noteText: chartNote,
        asyncReviewed,
        daysReviewed: daysReviewed === '' ? undefined : daysReviewed,
        asyncMinutes: asyncMinutes === '' ? undefined : asyncMinutes,
        asyncActivityType,
        syncContact,
        syncMinutes: syncMinutes === '' ? undefined : syncMinutes,
        syncMethod,
      });
      await medplum.createResource(withTenantScope(communication));
      savedSummary.push('Communication log entry created');

      await upsertRpmObservation(medplum, {
        patientId: entry.patientId,
        month,
        code: RPM_OBSERVATION_CODES.monthlyNote,
        existing: entry.rpmObservations[RPM_OBSERVATION_CODES.monthlyNote],
        effectiveDateTime: contactIso,
        value: { text: chartNote },
        note: chartNote,
      });

      setSummary(savedSummary);
      onSaved();
      notifications.show({ color: 'green', message: 'RPM note saved' });
    } catch (err) {
      setError(normalizeErrorString(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={entry ? `Add RPM note · ${entry.patientName}` : 'Add RPM note'}
      size="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Record this month's contact and save a note to the chart.
        </Text>
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        {summary.length > 0 && (
          <Alert color="green" variant="light" title="Saved">
            <Stack gap={2}>
              {summary.map((item) => (
                <Text key={item} size="sm">
                  {item}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        {summary.length === 0 ? (
          <>
            <Text fw={700}>This month's requirements</Text>
            <Checkbox
              label="Asynchronous data review"
              aria-label="Async reviewed — Asynchronous data review"
              description="Remote review of device usage / adherence data"
              checked={asyncReviewed}
              onChange={(event) => setAsyncReviewed(event.currentTarget.checked)}
            />
            {asyncReviewed && (
              <Stack gap="sm">
                <Select
                  label="Activity type"
                  data={asyncActivityData}
                  value={asyncActivityType}
                  onChange={(value) => setAsyncActivityType((value as RpmAsyncActivityType | null) ?? 'data-review')}
                />
                <Group grow align="flex-start">
                  <NumberInput
                    label="Days reviewed"
                    min={1}
                    max={30}
                    value={daysReviewed}
                    onChange={(value) => setDaysReviewed(typeof value === 'number' ? value : '')}
                  />
                  <NumberInput
                    label="Time spent (min)"
                    aria-label="Async minutes spent"
                    min={1}
                    value={asyncMinutes}
                    onChange={(value) => setAsyncMinutes(typeof value === 'number' ? value : '')}
                  />
                </Group>
              </Stack>
            )}
            <Checkbox
              label="Synchronous (live) contact"
              aria-label="Sync contact — Synchronous live contact"
              description="Phone or video conversation with the patient"
              checked={syncContact}
              onChange={(event) => setSyncContact(event.currentTarget.checked)}
            />
            {syncContact && (
              <Group grow align="flex-start">
                <Select
                  label="Contact method"
                  data={syncMethodData}
                  value={syncMethod}
                  onChange={(value) => setSyncMethod((value as RpmSyncContactMethod | null) ?? 'phone')}
                />
                <NumberInput
                  label="Time spent (min)"
                  aria-label="Sync minutes spent"
                  min={1}
                  value={syncMinutes}
                  onChange={(value) => setSyncMinutes(typeof value === 'number' ? value : '')}
                />
              </Group>
            )}
            <Textarea
              label="Note"
              description="Free text"
              minRows={4}
              placeholder="Document the interaction, patient-reported usage, barriers, and any coaching provided…"
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                color="polaris"
                aria-label="Save note — Add note to chart"
                loading={saving}
                onClick={() => handleSave().catch(console.error)}
              >
                Add note to chart
              </Button>
            </Group>
          </>
        ) : (
          <Group justify="flex-end">
            <Button color="polaris" onClick={onClose}>
              Done
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
